// Redaction-aware move/flip-cascade description for live gameplay --
// unlike the admin /dev/game-review tool (full unredacted access via
// turnLog), this must not name an animal it can't actually see: the
// opponent's still-face-down placement only ever shows an unknown swatch,
// never what it is. Everything else (moved/removed cards) is always safe
// to name, since attraction/Scorpion can only ever touch an already-
// face-up card.
//
// Returns an array of "parts" -- plain strings and `{ card: animal }`
// tokens marking where an inline MiniAnimalCard swatch goes -- rather than
// a plain string, so the caller (OasisBoard.js) can render the mix of text
// and icons. `animal: null` on a token means "unknown" (redacted).

import { ANIMAL_LABEL } from './oasisCatalog.js';

function cardToken(animal, faceDown) {
  return { card: animal, faceDown: Boolean(faceDown) };
}

// "You" possessive-izes irregularly ("Your", not "You's") -- everyone
// else's display name just takes a plain "'s".
function possessive(name) {
  return name === 'You' ? 'Your' : `${name}'s`;
}

// Per the spec: "<animal name with 's' for plural>" -- a plain trailing s,
// not attempting real English pluralization (e.g. "Oryxs").
function pluralLabel(animal) {
  return `${ANIMAL_LABEL[animal]}s`;
}

function formatColumnList(oasisIndexes) {
  const columns = [...new Set(oasisIndexes)].sort((a, b) => a - b).map(i => i + 1);
  if (columns.length === 1) return `Column ${columns[0]}`;
  if (columns.length === 2) return `Columns ${columns[0]} and ${columns[1]}`;
  return `Columns ${columns.slice(0, -1).join(', ')}, and ${columns[columns.length - 1]}`;
}

// Joins a list of `{ animal }`-bearing records as inline icon+name pairs,
// "X, Y, and Z" style (Oxford comma), for the "different animals" cases.
function joinCardList(records, animalOf) {
  const parts = [];
  records.forEach((r, i) => {
    const animal = animalOf(r);
    parts.push(cardToken(animal), ` ${ANIMAL_LABEL[animal]}`);
    if (i < records.length - 2) parts.push(', ');
    else if (i === records.length - 2) parts.push(records.length > 2 ? ', and ' : ' and ');
  });
  return parts;
}

function placementParts(placedCards, actorSide, opponentName) {
  // The icon always shows face-down styling for a face-down card (see
  // cardToken), but the text names it whenever the animal is actually
  // known -- my own hidden card (I can peek), just not the opponent's
  // still-hidden one (redacted, animal is null).
  const cardWithLabel = c => {
    const label = c.animal ? ANIMAL_LABEL[c.animal] : 'a card';
    return [cardToken(c.animal, c.face === 'down'), ` ${label}`];
  };

  if (placedCards.length === 1) {
    const c = placedCards[0];
    const parts = cardWithLabel(c);
    if (c.face === 'down') parts.push(' face-down');
    // Action A/C always land on the actor's own side -- only action B (an
    // attack onto the opponent's side) needs to say so explicitly.
    if (c.side !== actorSide) parts.push(` on ${sideLabel(c.side, opponentName)}`);
    parts.push(` in Column ${c.oasisIndex + 1}`);
    return parts;
  }

  const [a, b] = placedCards;
  // Action A's 2 cards are always face-up (from hand), so never redacted --
  // both animals are always known here, and 2 of the same animal reads as
  // "2 Jackals in Column(s) X[ and Y]" rather than naming it twice.
  if (a.animal && a.animal === b.animal) {
    const columns = formatColumnList([a.oasisIndex, b.oasisIndex]);
    return ['2 ', cardToken(a.animal), ` ${pluralLabel(a.animal)} in ${columns}`];
  }

  const sameColumn = a.oasisIndex === b.oasisIndex && a.side === b.side;
  if (sameColumn) {
    return [...cardWithLabel(a), ' and ', ...cardWithLabel(b), ` in Column ${a.oasisIndex + 1}`];
  }
  return [
    ...cardWithLabel(a),
    ` in Column ${a.oasisIndex + 1}`,
    ' and ',
    ...cardWithLabel(b),
    ` in Column ${b.oasisIndex + 1}`,
  ];
}

// The pulled-in ("attraction") clause -- every card here is necessarily
// the same animal as whichever placed/flipped card triggered it, but a
// move with 2 placed cards (or a cascade with several flips) can combine
// pulls of more than one animal overall.
function pullParts(movedCards) {
  if (movedCards.length === 0) return [];
  const animals = new Set(movedCards.map(m => m.animal));

  if (movedCards.length === 1) {
    const m = movedCards[0];
    return [' pulled ', cardToken(m.animal), ` ${ANIMAL_LABEL[m.animal]} from Column ${m.fromOasisIndex + 1}`];
  }
  if (animals.size === 1) {
    const animal = movedCards[0].animal;
    const columns = formatColumnList(movedCards.map(m => m.fromOasisIndex));
    return [` pulled ${movedCards.length} `, cardToken(animal), ` ${pluralLabel(animal)} from ${columns}`];
  }
  const columns = formatColumnList(movedCards.map(m => m.fromOasisIndex));
  return [' pulled ', ...joinCardList(movedCards, m => m.animal), ` from ${columns}`];
}

// 'my'/'opponent' is always from *this viewer's* perspective (already
// translated server-side), regardless of who actually made the move.
function sideLabel(side, opponentName) {
  return side === 'my' ? 'your side' : `${opponentName}'s side`;
}

// The Scorpion-kill clause -- a single Scorpion destroys at most one card
// per side of its own oasis (2 total), but a move/cascade with more than
// one Scorpion can combine kills from several. Which side each kill
// happened on matters here (unlike a pull, which is about a column, not a
// side) since a Scorpion always hits at most one card per side.
function destroyParts(removedCards, opponentName) {
  if (removedCards.length === 0) return [];
  const animals = new Set(removedCards.map(r => r.animal));

  if (removedCards.length === 1) {
    const r = removedCards[0];
    return [' destroyed ', cardToken(r.animal), ` ${ANIMAL_LABEL[r.animal]} on ${sideLabel(r.fromSide, opponentName)}`];
  }
  if (removedCards.length === 2 && animals.size === 1) {
    const animal = removedCards[0].animal;
    return [' destroyed ', cardToken(animal), ` ${ANIMAL_LABEL[animal]} on both sides`];
  }
  if (removedCards.length === 2) {
    // Different animals -- a Scorpion kills at most 1 per side, so this is
    // deterministically one from each side.
    const [r1, r2] = removedCards;
    return [
      ' destroyed ',
      cardToken(r1.animal),
      ` ${ANIMAL_LABEL[r1.animal]} on ${sideLabel(r1.fromSide, opponentName)}`,
      ' and ',
      cardToken(r2.animal),
      ` ${ANIMAL_LABEL[r2.animal]} on ${sideLabel(r2.fromSide, opponentName)}`,
    ];
  }
  if (animals.size === 1) {
    const animal = removedCards[0].animal;
    return [` destroyed ${removedCards.length} `, cardToken(animal), ` ${pluralLabel(animal)}`];
  }
  return [' destroyed ', ...joinCardList(removedCards, r => r.animal)];
}

// Matches each moved/removed card to whichever unit (a placed card, or a
// flipped card) actually caused it -- a pull by its destination column
// (attraction always pulls into the just-acted column), a kill only when
// the unit is itself a Scorpion (matched by its own column). Mirrors
// OasisBoard.js's interleaveEffectSteps, which attributes the same way for
// the animation sequence -- any leftover unattributed effect (shouldn't
// normally happen) is folded into the last unit rather than silently lost.
function attributeToUnits(units, movedCards, removedCards) {
  const remainingMoved = [...movedCards];
  const remainingRemoved = [...removedCards];
  const attributed = units.map(unit => {
    const ownMoved = [];
    for (let i = remainingMoved.length - 1; i >= 0; i--) {
      const m = remainingMoved[i];
      // Matching the destination column/side alone isn't enough when 2
      // placed/flipped cards share the same column (e.g. an Oryx and a
      // Jackal both placed in Column 1) -- attraction only ever pulls a
      // card of the *same animal* as whatever triggered it, so a Jackal
      // pull can never actually belong to an Oryx's own placement.
      if (m.toOasisIndex === unit.oasisIndex && m.toSide === unit.side && m.animal === unit.animal) {
        ownMoved.unshift(m);
        remainingMoved.splice(i, 1);
      }
    }
    const ownRemoved = [];
    if (unit.isScorpion) {
      for (let i = remainingRemoved.length - 1; i >= 0; i--) {
        const r = remainingRemoved[i];
        if (r.fromOasisIndex === unit.oasisIndex && r.fromSide === unit.side) {
          ownRemoved.unshift(r);
          remainingRemoved.splice(i, 1);
        }
      }
    }
    return { unit, ownMoved, ownRemoved };
  });
  if (attributed.length > 0) {
    attributed[attributed.length - 1].ownMoved.push(...remainingMoved);
    attributed[attributed.length - 1].ownRemoved.push(...remainingRemoved);
  }
  return attributed;
}

function placedCardClause(c, actorSide, opponentName) {
  // Icon shows face-down styling regardless; text names it only when the
  // animal is actually known -- see placementParts' cardWithLabel for why.
  const label = c.animal ? ANIMAL_LABEL[c.animal] : 'a card';
  const parts = [cardToken(c.animal, c.face === 'down'), ` ${label}`];
  if (c.face === 'down') parts.push(' face-down');
  // Action A/C always land on the actor's own side -- only action B (an
  // attack onto the opponent's side) needs to say so explicitly.
  if (c.side !== actorSide) parts.push(` on ${sideLabel(c.side, opponentName)}`);
  parts.push(` in Column ${c.oasisIndex + 1}`);
  return parts;
}

/**
 * Describes a single move's own placement + pulls + Scorpion kill. A move
 * with no pull/kill at all just states the placement(s) plainly; as soon
 * as either happened, each placed card gets its own "played X in Column Y"
 * clause with its own pull/kill attributed right after it (so a 2-card
 * move where only one card triggered anything doesn't read as if either
 * card might be responsible) -- separate clauses join as separate
 * sentences (only the first names the actor -- "Player played X... Played
 * Y..." -- not run together with "and").
 */
export function describeMove({ placedCards = [], movedCards = [], removedCards = [] }, actorName, actorSide, opponentName) {
  if (placedCards.length === 0) {
    return [`${actorName} made no board change.`];
  }
  if (movedCards.length === 0 && removedCards.length === 0) {
    return [`${actorName} played `, ...placementParts(placedCards, actorSide, opponentName)];
  }

  const units = placedCards.map(c => ({
    oasisIndex: c.oasisIndex,
    side: c.side,
    animal: c.animal,
    isScorpion: c.face === 'up' && c.animal === 'scorpion',
    card: c,
  }));
  const clauses = attributeToUnits(units, movedCards, removedCards).map(({ unit, ownMoved, ownRemoved }, i) => {
    const parts = [
      i === 0 ? `${actorName} played ` : 'Played ',
      ...placedCardClause(unit.card, actorSide, opponentName),
    ];
    const pull = pullParts(ownMoved);
    const destroy = destroyParts(ownRemoved, opponentName);
    if (pull.length > 0) parts.push(' and', ...pull);
    if (destroy.length > 0) parts.push(pull.length > 0 ? ', and' : ' and', ...destroy);
    return parts;
  });

  const parts = [];
  clauses.forEach((clause, i) => {
    if (i > 0) parts.push('. ');
    parts.push(...clause);
  });
  return parts;
}

function findCard(oasisRow, cardId) {
  for (let oasisIndex = 0; oasisIndex < oasisRow.length; oasisIndex++) {
    const oasis = oasisRow[oasisIndex];
    const mine = oasis.myColumn.find(c => c.cardId === cardId);
    if (mine) return { oasisIndex, side: 'my', card: mine };
    const theirs = oasis.opponentColumn.find(c => c.cardId === cardId);
    if (theirs) return { oasisIndex, side: 'opponent', card: theirs };
  }
  return null;
}

/**
 * Describes the round-end flip cascade -- every other face-down card
 * flipping, and whatever attraction/Scorpion effect that in turn
 * triggers, one flip at a time (each flip's own pull/kill is attributed to
 * it the same way OasisBoard.js's animation sequencer attributes them:
 * a pull by matching its destination column, a kill only when the
 * flipped card is itself a Scorpion, matched by its own column). Round-end
 * reveals both hands, so nothing here needs redacting; `nameFor` maps a
 * card's 'my'/'opponent' side to a display name. `oasisRow` should be the
 * fully-resolved post-cascade board (e.g. lastRoundResult.oasisRow) so
 * every flipped card is there to look up.
 */
export function describeFlipCascade({ flippedCardIds = [], movedCards = [], removedCards = [] }, oasisRow, nameFor) {
  if (flippedCardIds.length === 0) return [];
  const remainingMoved = [...movedCards];
  const remainingRemoved = [...removedCards];
  const opponentName = nameFor('opponent');
  const parts = [];

  // A run of consecutive "plain" flips (same side + animal, no pull/kill of
  // their own) reads as one combined sentence -- "Your Elephants flipped
  // face-up in Columns 2 and 4" -- instead of one sentence per card. A flip
  // with its own pull/kill always gets its own clause (that detail is
  // specific to that card), which also ends any run in progress.
  let bufferSide = null;
  let bufferAnimal = null;
  let bufferColumns = [];

  function flushBuffer() {
    if (bufferColumns.length === 0) return;
    if (parts.length > 0) parts.push(' ');
    const columns = formatColumnList(bufferColumns);
    const label = bufferColumns.length === 1 ? ANIMAL_LABEL[bufferAnimal] : pluralLabel(bufferAnimal);
    parts.push(`${possessive(nameFor(bufferSide))} `, cardToken(bufferAnimal), ` ${label} flipped face-up in ${columns}.`);
    bufferSide = null;
    bufferAnimal = null;
    bufferColumns = [];
  }

  for (const cardId of flippedCardIds) {
    const found = findCard(oasisRow, cardId);
    if (!found) continue;

    const ownMoved = [];
    for (let i = remainingMoved.length - 1; i >= 0; i--) {
      const m = remainingMoved[i];
      // Same-animal match required too -- see describeMove's identical
      // guard for why (2 different-animal cards flipping/landing in the
      // same column would otherwise be ambiguous by position alone).
      if (m.toOasisIndex === found.oasisIndex && m.toSide === found.side && m.animal === found.card.animal) {
        ownMoved.unshift(m);
        remainingMoved.splice(i, 1);
      }
    }
    const ownRemoved = [];
    if (found.card.animal === 'scorpion') {
      for (let i = remainingRemoved.length - 1; i >= 0; i--) {
        const r = remainingRemoved[i];
        if (r.fromOasisIndex === found.oasisIndex && r.fromSide === found.side) {
          ownRemoved.unshift(r);
          remainingRemoved.splice(i, 1);
        }
      }
    }

    const isPlain = ownMoved.length === 0 && ownRemoved.length === 0;
    if (isPlain && bufferSide === found.side && bufferAnimal === found.card.animal) {
      bufferColumns.push(found.oasisIndex);
      continue;
    }
    flushBuffer();
    if (isPlain) {
      bufferSide = found.side;
      bufferAnimal = found.card.animal;
      bufferColumns = [found.oasisIndex];
      continue;
    }

    if (parts.length > 0) parts.push(' ');
    parts.push(
      `${possessive(nameFor(found.side))} `,
      cardToken(found.card.animal),
      ` ${ANIMAL_LABEL[found.card.animal]} flipped face-up in Column ${found.oasisIndex + 1}`,
      ...pullParts(ownMoved),
      ...destroyParts(ownRemoved, opponentName),
      '.'
    );
  }
  flushBuffer();
  return parts.length > 0 ? parts : ['No cards flipped.'];
}
